import { getConfiguratorViewInfo } from '@configurator/view/configurator-view-info';
import { SceneManager } from '@3d-scene/manager/scene.manager';
import { UserModel } from '@user/model/user.model';
import { MountingAssistantService } from '@configuration/assistants/mounting/mounting-assistant.service';
import { ProjectUtils } from '../../components/project/util/project-utils';
import { ProjectModel } from '../../model/project.model';
import { MENU_SEPARATOR, MenuList } from './menu.interfaces';
import { LayerManager } from '@3d-scene/manager/layer-manager';
import { Injectable } from '@angular/core';
import { InterfaceMenuCreator } from './interface-menu.creator';
import { IsProjectType } from '../../model/is-project-type';
import { ExportToCabinetAssemblyRobotService } from '../../components/project/service/export-to-cabinet-assembly-robot.service';
import { ToastrService } from 'ngx-toastr';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { AlertHelper } from '@shared/alert/alert.helper';
import { ExportRailInfosService } from '../../components/project/service/export-rail-infos.service';
import { FileUtils } from '@shared/util/file-utils';
import { SaveProjectService } from '../../components/project/project-save/save-project.service';
import { JsonInfoService } from '../json-info.service';
import { PrintConverterService } from '../../components/project/service/print-converter.service';
import { BackgroundTasksInfoService } from '../background-tasks-info.service';

export type MountingMenuItemName =
  | 'mountingAssistant';

@Injectable({providedIn: 'root'})
export class MountingMenuCreator {

  constructor(
    private projectModel: ProjectModel,
    private userModel: UserModel,
    private mountingAssistantService: MountingAssistantService,
    private sceneManager: SceneManager,
    private layerManager: LayerManager,
    private interfaceMenuCreator: InterfaceMenuCreator,
    private exportToCabinetAssemblyRobotService: ExportToCabinetAssemblyRobotService,
    private toastrService: ToastrService,
    private localeResourceManager: LocaleResourceManager,
    private alertHelper: AlertHelper,
    private exportRailInfosService: ExportRailInfosService,
    private saveProjectService: SaveProjectService,
    private jsonInfoService: JsonInfoService,
    private printConverterService: PrintConverterService,
    private backgroundTasksInfoService: BackgroundTasksInfoService,
  ){}

  public create(): { id: string, content: MenuList } {
    return  { id: 'header-menu-mounting', content: this.getMountingItems() };
  }

  private getMountingItems(): MenuList {
    const items: MenuList = [
      {
        label: 'header.menu.start_mounting_assistant',
        action: () => this.startMountingAssistant(),
        enabled: this.isMountingAssistantEnabled(),
        visible: this.isMountingAssistantVisible(),
        appActionLogId: 'header.menu.start_mounting_assistant',
        id: 'header-menu-start-mounting-assistant',
      },
      {
        label: 'main.tabBar.view.label.toggle.drilling_hole_view',
        action: () => this.activateDrillingHoleDimensions(),
        enabled: true,
        visible: this.isMountingAssistantVisible(),
        appActionLogId: 'header.menu.toggle.drilling_hole_view',
        id: 'header-menu-toggle-drilling-hole-view',
      },
      this.interfaceMenuCreator.createMenuItemPrintLabel(),
      MENU_SEPARATOR,
      {
        label: 'header.menu.car.export',
        action: () => this.exportCarFile(),
        enabled: this.isCarExportEnabled(),
        visible: this.isCarExportVisible(),
        appActionLogId: 'header.menu.car.export',
        id: 'header-menu-project-car-export',
      },
      {
        label: 'header.menu.export_rail_xml',
        action: () => this.exportRailXml(),
        enabled: true,
        visible: this.isExportRailXmlVisible(),
        appActionLogId: 'header-menu-project-export-mounting-rail-xml',
        id: 'header-menu-project-rail-xml-export',
      },
      {
        label: 'header.menu.export_direct_prints',
        action: () => this.exportDirectPrintingsToPdf(),
        enabled: !this.backgroundTasksInfoService.isTaskActive('printconverter-export'),
        visible: this.isExportDirectPrintingsToPdfVisible(),
        appActionLogId: 'header.menu.export_direct_prints',
        id: 'header-menu-project-export-direct-prints',
      },
      {
        label: 'header.menu.export_ltl_info_json',
        action: () => this.generateLtlInfoJsonFile(),
        enabled: true,
        visible: this.isExportToLtlInfoVisible(),
        appActionLogId: 'header.menu.export_ltl_info_file',
      },
    ];

    return items;
  }

  private isMountingAssistantEnabled(): boolean {
    return !ProjectUtils.isEmptyProject(this.projectModel.currentProject)
      && IsProjectType.visibleMountingRail(this.projectModel.currentProject);
  }

  private isMountingAssistantVisible(): boolean {
    return this.userModel.user.hasPermission("VIEW_MOUNTING_ASSISTANT") &&
      getConfiguratorViewInfo(this.projectModel.currentProjectType).menu.mountingMenuItemsVisibility.mountingAssistant;
  }

  private startMountingAssistant(): void {
    this.mountingAssistantService.start('mounting-rail');
  }

  public activateDrillingHoleDimensions(): void {
    this.sceneManager.sceneControls.view('top');
    this.layerManager.activate('drillingHoles');
  }

  private exportCarFile(): void {
    try {
      this.exportToCabinetAssemblyRobotService.downloadJson();
      this.toastrService.success(this.localeResourceManager.getValueByKey("exportToAssemblyRobot.title.success"));
    } catch (error) {
      if(error instanceof Error) {
        this.alertHelper.showError("exportToAssemblyRobot.title.error", error.toString());
      }
    }
  }

  private isCarExportVisible(): boolean {
    return this.userModel.checkPermissions(["CAR_EXPORT"], false)
      && IsProjectType.visibleMountingRail(this.projectModel.currentProject);
  }

  private isCarExportEnabled(): boolean {
    if (ProjectUtils.isEmptyProject(this.projectModel.currentProject) || this.projectModel.isNewProject) {
      return false;
    }
    return !this.projectModel.isCurrentProjectChanged;
  }

  private exportRailXml() {
    this.exportRailInfosService.downloadRailXmlDoc();
  }

  private isExportRailXmlVisible() {
    return IsProjectType.visibleMountingRail(this.projectModel.currentProjectType)
      && this.userModel.user.isAdmin();
  }

  private isExportToLtlInfoVisible(): boolean {
    return IsProjectType.ltl(this.projectModel.currentProjectType)
      && this.userModel.user?.isAdmin();
  }

  private async generateLtlInfoJsonFile(): Promise<void> {
    if (await this.saveProjectService.saveProjectBeforeAction('main.message.request_save')) {
      const projectInfoJson: string = this.jsonInfoService.createProjectInfoJson(this.projectModel.currentProject);
      FileUtils.saveBinaryFile(this.projectModel.currentProject.name, projectInfoJson, '.json');
    }
  }

  private async exportDirectPrintingsToPdf(): Promise<void> {
    await this.printConverterService.exportDirectPrintingsToPdf();
  }

  private isExportDirectPrintingsToPdfVisible(): boolean {
    return this.userModel.user.hasPermission('EXPORT_TO_PRINT_CONVERTER')
      && (IsProjectType.mcsGds(this.projectModel.currentProject)
        || IsProjectType.ltl(this.projectModel.currentProject));
  }
}
